import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { IProductivityCoverage } from '@interfaces/IProductivity';
import { CheckIcon, CloseIcon } from '@utils/icons';
import { coverageLines } from './coverageLines';
import styles from './CoverageChecklist.module.css';

interface ICoverageChecklistProps {
  coverage: IProductivityCoverage;
  siteId?: string | undefined; // Enables the "open the Scheduler" link — where the prerequisites are authored
}

/**
 * The ✓/✗ prerequisite list plus the route to fix it.
 *
 * Shared by the two places coverage is explained — the page takeover when
 * nothing is measurable, and the expanded coverage bar when only some of it is —
 * so both tell an identical story at different weights.
 */
export function CoverageChecklist({ coverage, siteId }: ICoverageChecklistProps) {
  const { t } = useTranslation();

  return (
    <>
      <ul className={styles.list}>
        {coverageLines(coverage).map(({ prerequisite, satisfied, count }) => (
          <li key={prerequisite} className={styles.line} data-satisfied={satisfied}>
            <span className={styles.mark} aria-hidden="true">
              {satisfied ? <CheckIcon size={14} /> : <CloseIcon size={14} />}
            </span>
            <span className={styles.lineBody}>
              <span className={styles.lineText}>
                {t(`productivity.coverage.${satisfied ? 'ok' : 'missing'}.${prerequisite}`, { count })}
              </span>
              {!satisfied && (
                <span className={styles.lineWhy}>
                  {t(`productivity.coverage.missing.${prerequisite}Why`)}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {siteId && (
        <Link className={styles.cta} to={`/scheduler/${siteId}`}>
          {t('productivity.coverage.cta')}
        </Link>
      )}
    </>
  );
}